import { useState } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';

export interface ArticleSection { h2: string; body: string[]; }
export interface ArticleFAQ { q: string; a: string; }
export interface ArticleRelated { title: string; href: string; }

export interface BlogArticleProps {
  slug: string;
  seoTitle: string;
  metaDescription: string;
  h1: string;
  category: string;
  datePublished: string;
  dateLabel: string;
  readingTime: string;
  heroImage: string;
  heroAlt: string;
  intro: string[];
  sections: ArticleSection[];
  faq: ArticleFAQ[];
  related: ArticleRelated[];
  ctaTitle: string;
  ctaText: string;
}

export default function BlogArticle(p: BlogArticleProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const pageUrl = `https://depanfenetres-lyon.fr/blog/${p.slug}/`;

  const blogJsonLd = {
    '@context': 'https://schema.org', '@type': 'BlogPosting', headline: p.seoTitle,
    description: p.metaDescription, image: `https://depanfenetres-lyon.fr${p.heroImage}`,
    datePublished: p.datePublished, dateModified: p.datePublished,
    author: { '@type': 'Organization', name: 'DFPRO Lyon' },
    publisher: { '@type': 'Organization', name: 'DFPRO Lyon', logo: { '@type': 'ImageObject', url: 'https://depanfenetres-lyon.fr/images/dfpro-hero-2026.jpg' } },
    mainEntityOfPage: pageUrl, url: pageUrl,
  };
  const faqJsonLd = { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: p.faq.map((i) => ({ '@type': 'Question', name: i.q, acceptedAnswer: { '@type': 'Answer', text: i.a } })) };
  const breadcrumbJsonLd = { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://depanfenetres-lyon.fr/' },
    { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://depanfenetres-lyon.fr/blog/' },
    { '@type': 'ListItem', position: 3, name: p.category, item: pageUrl },
  ] };

  return (
    <>
      <SEOHead title={p.seoTitle} description={p.metaDescription} canonicalUrl={pageUrl} ogImage={`https://depanfenetres-lyon.fr${p.heroImage}`} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(blogJsonLd) }}></script>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}></script>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}></script>
      <Header />
      <main>
        <section className="relative w-full min-h-[300px] md:min-h-[380px] flex items-center">
          <div className="absolute inset-0">
            <img src={p.heroImage} alt={p.heroAlt} className="w-full h-full object-cover object-top" loading="eager" />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/60 via-foreground-900/40 to-foreground-900/60"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-12 md:py-16">
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block text-xs font-medium text-accent-300 bg-accent-500/20 px-3 py-1 rounded-full mb-3">{p.category}</span>
              <h1 className="font-heading font-bold text-xl md:text-3xl lg:text-4xl text-background-50 leading-tight">{p.h1}</h1>
              <p className="mt-3 text-sm text-background-50/70">Publié le {p.dateLabel} — Temps de lecture : {p.readingTime}</p>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-3 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-1.5 text-xs md:text-sm flex-wrap" aria-label="Fil d'ariane">
              <a href="/" className="text-secondary-500 hover:text-primary-500 transition-colors">Accueil</a><span className="text-secondary-400">/</span>
              <a href="/blog/" className="text-secondary-500 hover:text-primary-500 transition-colors">Blog</a><span className="text-secondary-400">/</span>
              <span className="text-foreground-700 font-medium">{p.category}</span>
            </nav>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="prose-article space-y-5">
              {p.intro.map((t, i) => (
                <p key={`i-${i}`} className="text-base text-secondary-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: t }} />
              ))}
            </div>

            {p.sections.map((s, si) => (
              <div key={`s-${si}`} className="mt-10">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-4">{s.h2}</h2>
                <div className="space-y-4">
                  {s.body.map((t, bi) => (
                    <p key={`b-${si}-${bi}`} className="text-base text-secondary-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: t }} />
                  ))}
                </div>
                {si === 0 && (
                  <div className="my-8 bg-primary-500 rounded-xl p-6 md:p-7 text-center">
                    <p className="font-heading font-bold text-lg text-background-50">{p.ctaTitle}</p>
                    <p className="mt-1.5 text-sm text-background-50/80">{p.ctaText}</p>
                    <a href="tel:+33980803684" className="mt-4 inline-flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm px-6 py-3 rounded-md transition-colors">
                      <i className="ri-phone-fill w-4 h-4 flex items-center justify-center"></i>Appeler le 09 80 80 36 84
                    </a>
                  </div>
                )}
              </div>
            ))}

            <div className="mt-12">
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-4">Questions fréquentes</h2>
              <div className="space-y-3">
                {p.faq.map((f, fi) => (
                  <div key={`f-${fi}`} className="border border-background-200 rounded-lg overflow-hidden">
                    <button onClick={() => setOpenFaq(openFaq === fi ? null : fi)} className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left cursor-pointer">
                      <span className="font-medium text-sm text-foreground-800">{f.q}</span>
                      <i className={`ri-arrow-down-s-line text-secondary-500 w-5 h-5 flex items-center justify-center transition-transform ${openFaq === fi ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openFaq === fi && (<div className="px-4 pb-4 text-sm text-secondary-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: f.a }} />)}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-12 bg-primary-500 rounded-xl p-7 md:p-8 text-center">
              <h2 className="font-heading font-bold text-xl md:text-2xl text-background-50">Un dépannage à Lyon ? DFPRO intervient 7j/7</h2>
              <p className="mt-2 text-sm text-background-50/80">Devis gratuit · Garantie 10 ans · Intervention 24-48h</p>
              <a href="tel:+33980803684" className="mt-5 inline-flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm px-7 py-3.5 rounded-md transition-colors">
                <i className="ri-phone-fill w-4 h-4 flex items-center justify-center"></i>09 80 80 36 84
              </a>
            </div>

            {p.related.length > 0 && (
              <div className="mt-12">
                <h2 className="font-heading font-bold text-lg text-foreground-900 mb-4">À lire aussi</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {p.related.map((r, ri) => (
                    <a key={`r-${ri}`} href={r.href} className="block border border-background-200 rounded-lg px-4 py-3 hover:border-primary-300 transition-colors">
                      <span className="text-sm font-medium text-primary-600">{r.title}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
